import { Component, ErrorHandler, OnDestroy, OnInit, ChangeDetectionStrategy, signal, computed, inject } from '@angular/core'
import { toSignal } from '@angular/core/rxjs-interop'
import { AbstractControl } from '@angular/forms'
import { applyEach, FieldTree, form, required, validate } from '@angular/forms/signals'
import { ActivatedRoute, Router } from '@angular/router'

import { MatDialog } from '@angular/material/dialog'

import { asetaArvo, nollaa } from '../_jaettu-angular/_lomakkeet/arvon-muutokset'
import { validoiReaktiivisesti } from '../_jaettu-angular/_lomakkeet/reaktiivinen-validaattori'
import { FormValidationService } from '../_jaettu-angular/service/form-validation.service'
import { LadataanService } from '../_jaettu-angular/service/ladataan.service'

import { LaskuToimittaja, LaskuToimittajaIban, ReplikoiToimittajaTyojonoData } from '@lemon/core-firebase/model/lasku'

import { MaksutToimittajaPoistoVarmistusDialog, MaksutToimittajaPoistoVarmistusDialogData } from './maksut-toimittaja-poisto-varmistus.dialog'

import { MatRadioChange } from '@angular/material/radio'
import { LaskuUriService } from '@lemon/core-firebase/service/lasku/lasku-uri.service'
import { LaskuKopioija } from '@lemon/core-firebase/service/lasku/lasku.kopioija'
import { ToimittajaTunnisteService } from '@lemon/core-firebase/service/lasku/toimittaja-tunniste.service'
import { WebToimittajaYtjTiedotPyynto, WebToimittajaYtjTiedotVastaus } from '@lemon/core-firebase/model/web-toimittaja-api'
import { BicService } from '@lemon/core/service/bic.service'
import { CurrencyService } from '@lemon/core/service/currency.service'
import { StringService } from '@lemon/core/service/string.service'
import { LemonaidValidators } from 'app/_angular/_validator/LemonaidValidators'
import { FirebaseLemonaid } from 'app/_angular/service/firebase-lemonaid.service'
import { firstValueFrom, of, Subject } from 'rxjs'
import { catchError, filter, switchMap, takeUntil, timeout } from 'rxjs/operators'
import { KayttajaService } from '../_angular/service/kayttaja.service'
import { LaskuService } from '../_angular/service/lasku/lasku.service'
import { LemonTranslationService } from '../_jaettu-angular/service/lemon-translation.service'
import { tunnisteValidator } from '../tositteet/lisaa-tositteita/toimittaja-kentta/toimittaja-kentta.component'

interface ToimittajaLomake {
  numero: number | null
  nimi: string
  maksunSaaja: string
  ytunnus: string
  katuosoite: string
  postinro: string
  postitmp: string
  ibans: LaskuToimittajaIban[]
}

const TYHJA_LOMAKE: ToimittajaLomake = { numero: null, nimi: '', maksunSaaja: '', ytunnus: '', katuosoite: '', postinro: '', postitmp: '', ibans: [] }

@Component({
  selector: 'app-toimittaja',
  templateUrl: './maksut-toimittaja.component.html',
  styleUrls: ['./maksut-toimittaja.component.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class MaksutToimittajaComponent implements OnInit, OnDestroy {
  private ngUnsubscribe: Subject<void> = new Subject<void>()
  private readonly _alkuperainenMuokattavaTunniste = signal<string | null>(null)
  _basePath: string
  maksutPuoli: boolean

  lomake: FieldTree<ToimittajaLomake>
  private readonly _kieli = toSignal(inject(LemonTranslationService).currentLanguageObservable)
  readonly fieldErrors = computed(() => {
    // Virheviestit käännetään nykyisellä kielellä, joten kielen vaihto muodostaa ne uudelleen
    this._kieli()
    return this.validationService.updateValidationStatus('maksut.toimittajat', this.lomake)
  })
  readonly toimittaja = signal<LaskuToimittaja>(null)
  readonly ytjHakuKaynnissa = signal(false)
  readonly ytjViesti = signal<string | null>(null)
  readonly ytjVirhe = signal(false)
  readonly muutTunnisteet = signal<string[]>([])

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private dialog: MatDialog,
    private validationService: FormValidationService,
    private errorHandler: ErrorHandler,
    private ladataanService: LadataanService,
    private laskuService: LaskuService,
    private laskuUriService: LaskuUriService,
    private laskuKopioija: LaskuKopioija,
    private bicService: BicService,
    private currencyService: CurrencyService,
    private kayttajaService: KayttajaService,
    private _firebaseLemonaid: FirebaseLemonaid,
    private _stringService: StringService,
    private _toimittajaTunnisteService: ToimittajaTunnisteService,
    private _translationService: LemonTranslationService,
  ) {
    this.toimittaja.set(laskuKopioija.annaUusiToimittaja())
    const currentUrl = this.router.url
    this._basePath = currentUrl.startsWith('/tositteet') ? '/tositteet/toimittajat' : '/maksut/toimittajat'
    this.maksutPuoli = this._basePath === '/maksut/toimittajat'
    const tunnisteenTarkistus = tunnisteValidator(this._toimittajaTunnisteService)
    // Tallennettu ALV-tunnus voi tulla verkkolaskulta muodossa, jota tarkistus ei tunnista,
    // eikä sen koskematon arvo saa estää muiden tietojen tallennusta.
    this.lomake = form(signal(TYHJA_LOMAKE), polku => {
      required(polku.nimi)
      validate(polku.ytunnus, ({ value }) => {
        const alkuperainen = this._alkuperainenMuokattavaTunniste()
        if (alkuperainen && this.validationService.processValue(value()) === alkuperainen) {
          return null
        }
        const virheet = tunnisteenTarkistus({ value: value() } as AbstractControl)
        return virheet ? Object.keys(virheet).map(kind => ({ kind })) : null
      })
      // Maksun saajaa tarvitaan vain maksuissa; ostotositteilla toimittajalta riittää nimi.
      if (this.maksutPuoli) {
        required(polku.maksunSaaja)
      }
      applyEach(polku.ibans, rivi => {
        validoiReaktiivisesti(rivi.iban, LemonaidValidators.ibanValidator)
        validoiReaktiivisesti(rivi.bic, LemonaidValidators.bicValidator)
        required(rivi.tyyppi)
      })
    })
  }

  ngOnDestroy() {
    this.ngUnsubscribe.next()
    this.ngUnsubscribe.complete()
  }

  ngOnInit() {
    this.route.data.pipe(takeUntil(this.ngUnsubscribe)).subscribe(({ data }) => {
      nollaa(this.lomake, TYHJA_LOMAKE)
      this.ytjViesti.set(null)
      this.ytjVirhe.set(false)

      const toimittaja = this.laskuKopioija.copyToimittaja(data)
      this.toimittaja.set(toimittaja)

      if (this.maksutPuoli && toimittaja.iban) {
        asetaArvo(
          this.lomake.ibans,
          Object.values(toimittaja.iban).map(ibanData => ({ iban: ibanData.iban ?? '', bic: ibanData.bic ?? '', tyyppi: ibanData.tyyppi })),
        )
      }

      const nykyisetTunnisteet = this._toimittajaTunnisteService.nykyisetTunnisteet(toimittaja)
      this._alkuperainenMuokattavaTunniste.set(this._toimittajaTunnisteService.annaMuokattavaTunniste(nykyisetTunnisteet))
      this.muutTunnisteet.set(this._toimittajaTunnisteService.annaMuutTunnisteet(nykyisetTunnisteet))
      asetaArvo(this.lomake.numero, this.currencyService.onkoNumero(toimittaja.numero) ? toimittaja.numero : null)
      asetaArvo(this.lomake.nimi, toimittaja.nimi || '')
      asetaArvo(this.lomake.maksunSaaja, toimittaja.maksunSaaja || '')
      asetaArvo(this.lomake.ytunnus, this._alkuperainenMuokattavaTunniste() ?? '')
      asetaArvo(this.lomake.katuosoite, toimittaja.katuosoite || '')
      asetaArvo(this.lomake.postinro, toimittaja.postinro || '')
      asetaArvo(this.lomake.postitmp, toimittaja.postitmp || '')
    })
  }

  lisaaIban() {
    const ibans = this.lomake.ibans().value()
    const tyyppi = ibans.length === 0 ? 'oletus' : 'tavallinen'
    asetaArvo(this.lomake.ibans, [...ibans, { iban: '', bic: '', tyyppi }])
  }

  ibanMuuttui(syotettyIban: string, bicKentta: FieldTree<string> = null) {
    const iban = this._stringService.removeAllWhiteSpaces(syotettyIban)
    const bic = this.bicService.annaBicKoodi(iban)
    if (bic && bicKentta) {
      asetaArvo(bicKentta, bic)
    }
  }

  poistaIban(index: number) {
    const ibans = this.lomake.ibans().value()
    const onkoOletus = ibans[index]?.tyyppi === 'oletus'
    const jaljella = ibans.filter((_, i) => i !== index)

    // Oletus poistetaan, lisää se ensimmäiselle, jos on olemassa
    asetaArvo(this.lomake.ibans, onkoOletus && jaljella.length > 0 ? [{ ...jaljella[0], tyyppi: 'oletus' }, ...jaljella.slice(1)] : jaljella)
  }

  onkoVirhe<T>(kentta: FieldTree<T>, laji: string): boolean {
    return kentta()
      .errors()
      .some(virhe => virhe.kind === laji)
  }

  annaOletusIbanIndex(): number {
    return this.lomake
      .ibans()
      .value()
      .findIndex(rivi => rivi.tyyppi === 'oletus')
  }

  asetaOletusIbaniksi(event: MatRadioChange) {
    const index = event.value as number

    if (typeof index === 'number') {
      // Valittu oletukseksi, kaikki muut 'tavallisiksi'
      asetaArvo(
        this.lomake.ibans,
        this.lomake
          .ibans()
          .value()
          .map((rivi, i) => ({ ...rivi, tyyppi: i === index ? 'oletus' : 'tavallinen' })),
      )
    }
  }

  async taydennaYtjsta() {
    this.ytjViesti.set(null)
    this.ytjVirhe.set(false)
    const ytunnus = this.validationService.processValue(this.lomake.ytunnus().value())
    if (!ytunnus) {
      this._naytaYtjVirhe('ytunnus-puuttuu')
      return
    }

    this.ytjHakuKaynnissa.set(true)
    try {
      const vastaus = await this._firebaseLemonaid.functionsCall<WebToimittajaYtjTiedotPyynto, WebToimittajaYtjTiedotVastaus>('toimittajaYtjTiedotWeb', { ytunnus }, undefined, 'europe-north1')
      if (vastaus?.e || !vastaus?.toimittaja) {
        this._naytaYtjVirhe(vastaus?.e ?? 'virhe')
        return
      }

      const ytjToimittaja = vastaus.toimittaja
      const kentat = this.lomake
      let taydennettiin = false
      for (const [kentta, arvo] of [
        [kentat.nimi, ytjToimittaja.nimi],
        [kentat.maksunSaaja, ytjToimittaja.nimi],
        [kentat.katuosoite, ytjToimittaja.katuosoite],
        [kentat.postinro, ytjToimittaja.postinro],
        [kentat.postitmp, ytjToimittaja.postitmp],
      ] as const) {
        if (arvo && !this.validationService.processValue(kentta().value())) {
          asetaArvo(kentta, arvo)
          kentta().markAsDirty()
          taydennettiin = true
        }
      }
      this.ytjViesti.set(this._translationService.lokalisoi(taydennettiin ? 'maksut.toimittajat.ytj-taydennetty' : 'maksut.toimittajat.ytj-ei-taydennettavaa'))
    } catch (err) {
      this._naytaYtjVirhe('virhe')
      this.errorHandler.handleError(err)
    } finally {
      this.ytjHakuKaynnissa.set(false)
    }
  }

  private _naytaYtjVirhe(virhe: string) {
    this.ytjVirhe.set(true)
    this.ytjViesti.set(this._translationService.lokalisoi(`maksut.toimittajat.ytj-virheet.${virhe}`))
  }

  private _muodostaTunnistekentat() {
    const kentanTunniste = this.validationService.processValue(this.lomake.ytunnus().value()) || null
    if (kentanTunniste === this._alkuperainenMuokattavaTunniste()) {
      return { ytunnus: this.toimittaja().ytunnus, tunnisteetData: this.toimittaja().tunnisteetData, tunnisteet: this.toimittaja().tunnisteet }
    }
    const nykyisetTunnisteet = this._toimittajaTunnisteService.nykyisetTunnisteet(this.toimittaja())
    const tunnisteetData = this._toimittajaTunnisteService.vaihdaMuokattavaTunniste(nykyisetTunnisteet, kentanTunniste)
    return this._toimittajaTunnisteService.muodostaTunnistekentatDatasta(tunnisteetData)
  }

  poista() {
    const data: MaksutToimittajaPoistoVarmistusDialogData = {
      toimittaja: this.toimittaja(),
      odotaReplikointiTyojononKasittelyObservable: this.odotaReplikointiTyojononKasittely(this.toimittaja().avain),
    }
    this.dialog
      .open(MaksutToimittajaPoistoVarmistusDialog, { data: data })
      .afterClosed()
      .subscribe(result => {
        if (result) {
          this.router.navigate([this._basePath])
        }
      })
  }

  async save() {
    if (!this.lomake().valid()) {
      this.validationService.merkitseKokoLomakeKosketuksi(this.lomake)
      return
    }

    try {
      this.ladataanService.aloitaLataaminen()

      const tunnistekentat = this._muodostaTunnistekentat()

      const arvot = this.lomake().value()
      const toimittaja: LaskuToimittaja = {
        ...this.toimittaja(),
        numero: arvot.numero,
        nimi: this.validationService.processValue(arvot.nimi),
        ytunnus: tunnistekentat.ytunnus,
        tunnisteetData: tunnistekentat.tunnisteetData,
        tunnisteet: tunnistekentat.tunnisteet,
      }

      // Kuittipuolen näkymä näyttää vain nimen ja tunnisteen; muut tallennetut tiedot säilytetään sellaisenaan.
      if (this.maksutPuoli) {
        const ibanObj: { [iban: string]: LaskuToimittajaIban } = {}
        arvot.ibans.forEach(rivi => {
          const iban = this._stringService.removeAllWhiteSpaces(rivi.iban)
          const bic = this._stringService.removeAllWhiteSpaces(rivi.bic)
          const tyyppi = rivi.tyyppi
          if (iban) {
            ibanObj[iban] = { iban, bic, tyyppi }
          }
        })
        toimittaja.maksunSaaja = this.validationService.processValue(arvot.maksunSaaja)
        toimittaja.katuosoite = this.validationService.processValue(arvot.katuosoite) || null
        toimittaja.postinro = this.validationService.processValue(arvot.postinro) || null
        toimittaja.postitmp = this.validationService.processValue(arvot.postitmp) || null
        toimittaja.iban = ibanObj
      } else if (!toimittaja.maksunSaaja) {
        toimittaja.maksunSaaja = toimittaja.nimi
      }

      // Numero ei näy käyttäjälle, joten puuttuva tai jo varattu numero korvataan seuraavalla vapaalla.
      const toimittajaErrors = await this.laskuService.validoiToimittaja(this.toimittaja(), toimittaja)
      if (!toimittaja.numero || toimittajaErrors.numero) {
        const kayttajanTiedot = await this.kayttajaService.getKayttajanTiedot()
        toimittaja.numero = await this.laskuService.annaSeuraavaToimittajaNumero(kayttajanTiedot.asiakasAvain)
      }

      await this.laskuService.updateToimittajat([toimittaja])
      await firstValueFrom(this.odotaReplikointiTyojononKasittely(toimittaja.avain))

      this.ladataanService.lopetaLataaminen()
      this.router.navigate([this._basePath])
    } catch (err) {
      this.ladataanService.lopetaLataaminen()
      this.errorHandler.handleError(err)
    }
  }

  /**
   * Kuuntelija, joka ilmoittaa kun toimittajan replikointityöjono on myös käsitelty
   * (käyttöliittymä näyttää PostgreSQL-kannan tietoja, joten haluamme, että tiedot ovat siellä asti ennen kun palataan näytölle)
   */
  private odotaReplikointiTyojononKasittely(
    toimittajaAvain: string,
    odotaMaxMillis: number = 10000, // Cold start tilanteissa replikointityöjono voi olla hidas, joten asetetaan odotusaika suosiolla isommaksi
  ) {
    return this.kayttajaService.kayttajaObservable.pipe(
      filter(kayttaja => !!kayttaja?.asiakasId),
      switchMap(kayttaja =>
        this._firebaseLemonaid
          .firestoreCollection<ReplikoiToimittajaTyojonoData>(this.laskuUriService.getToimittajaReplikointiTyojonoCollectionUri(kayttaja.asiakasId + ''))
          .where('toimittajaAvain', '==', toimittajaAvain)
          .listenSnapshots()
          .pipe(
            // Palautetaan vain, kun collection tyhjä (eli työjonossa ei ole kyseiseen toimittajaan liittyvää käsittelyä)
            filter(snapshots => snapshots.length === 0),
            // Jos työjono collection ei tyhjene max. odotusajassa, jatketaan eteenpäin
            timeout(odotaMaxMillis),
            // Jos suoritus kaatuu, annetaan suorituksen jatkua, jottei käyttöliittymä jää jumiin
            catchError(() => of(true)),
          ),
      ),
    )
  }
}
